import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldArray, getFormValues } from 'redux-form/immutable';
import {
  Avatar,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
} from '@material-ui/core';
import { List as ImmutableList } from 'immutable';
import { useHistory } from 'react-router';
import {
  calculateFees,
  assertFeeCap,
  DonationProvider,
  PaymentMethodType,
  FeeCoveredBy,
  DEFAULT_DEED_FEE_CAP_USD,
  DEFAULT_CORPORATE_DONATION_FEE_CAP_USD,
  DEFAULT_APPLICATION_FEE_CORPORATE,
  DEFAULT_EXCLUDE_DONATION_PROVIDERS_CORPORATE_DONATION,
} from '@joindeed/calculate-fees';
import { format } from 'date-fns';
import { convertTokens } from '@date-fns/upgrade/v2';

import FormNonprofitsSelectorRedesign from 'retired/FormNonprofitsSelectorRedesign';
import FormTextFieldRedesign from 'retired/FormTextFieldRedesign';
import H4 from 'retired/H4';
import { selectBudgets } from 'entities/budget/selectors';
import { selectActiveBudgetAllocations } from 'entities/budgetAllocation/selectors';
import { selectUser } from 'entities/user/selectors';
import { formatAmount } from 'utils/format';
import { maxLength } from 'utils/validators';
import { SummarySegment } from 'retired/SummaryCard/SummarySegment';
import { SummaryCardImage } from 'retired/SummaryCard/SummaryCardImage';
import { SummaryCardHeader } from 'retired/SummaryCard/SummaryCardHeader';
import { SummaryCard } from 'retired/SummaryCard';
import { lightGray, lightYellow, white } from 'utils/color-styles';
import { PrimaryButton } from 'retired/PrimaryButton';
import DonationProviderMessage from 'retired/DonationProviderMessage';
import { useCurrency } from 'retired/CurrencyFormat';
import { AmountCurrencies, CurrencyCode, UserCurrencies } from 'utils/currencyFormat';
import { estimateCorporateDonationDisbursedAt } from 'utils/estimateCorporateDonationDisbursedAt';
import config from 'config';
import Organization from 'entities/organization/model';
import DonationsApi, { DonationProviderData } from 'entities/donation/api';
import { useQuery } from 'entities/useQuery';
import { TypedMap } from 'utils/typed-map';
import type User from 'entities/user/model';

import { BudgetSubEntitySelector } from './BudgetSubEntitySelector';
import { DonationToChaptersWarningMessage } from './DonationToChaptersWarningMessage';
import { CurrencyAmountEstimationMessage } from './CurrencyAmountEstimationMessage';

const DonationTotalForValidator = () => null;

// TODO: Refactor and fix types
const validateBudgetRule = (value: unknown) => {
  if (!value) {
    return true;
  }

  return undefined;
};

const validateNonprofits = (value: ImmutableList<Organization>) => !value || !value.size;

const validateDesignation = maxLength(300);

const validateNote = maxLength(300);

const ALLOWED_NONPROFIT_SELECTION_LIMIT = 1;

type CorporateDonationDonateValues = {
  nonprofits: Organization[];
  amount: number;
  budgetAllocation: string;
  donationTotalForValidator: boolean;
  donationProvider: DonationProvider;
  designation: string;
  note: string;
  customApplicationFeeAmount: number;
  customProviderFeeAmount: number;
  currencyCode: string;
  isAnonymous: boolean;
};

export interface CorporateDonationDonateFormProps {
  invalid: boolean;
  pristine: boolean;
  submitting: boolean;
  form: string;
  change: (a: keyof CorporateDonationDonateValues, b: any) => void;
}

export interface CorporateDonationDonateFormInnerProps extends CorporateDonationDonateFormProps {
  user: User;
  usdRates: AmountCurrencies;
  currencyRates: AmountCurrencies;
}

const validateAmount = (
  value: number,
  allValues: TypedMap<{ budgetAllocation: string; donationTotalForValidator: boolean }>
) => {
  const { budgetAllocation: allocation, donationTotalForValidator: donationTotalValidatorFailed } =
    allValues.toJS() as {
      budgetAllocation: string;
      donationTotalForValidator: boolean;
    };

  if (allocation) {
    if (!donationTotalValidatorFailed) {
      return 'Donation amount exceeds remaining budget balance';
    }
  }

  const currentInputValue = Number(value);

  return Number.isNaN(currentInputValue) || currentInputValue <= 0;
};

export const CorporateDonationDonateFormInner = ({
  invalid,
  pristine,
  submitting,
  form,
  change,
  user,
  usdRates,
  currencyRates,
}: CorporateDonationDonateFormInnerProps): JSX.Element => {
  const history = useHistory();
  const formValues = useSelector(getFormValues(form)) as unknown as CorporateDonationDonateValues;
  const budgets = useSelector(selectBudgets);
  const budgetAllocations = useSelector(selectActiveBudgetAllocations);

  let budgetAllocationBalance = 0;
  let budgetAllocationCurrencyRate = 1;

  const {
    nonprofits = [],
    amount,
    budgetAllocation,
    customApplicationFeeAmount,
    customProviderFeeAmount,
  } = formValues?.toJS() || {};

  const budgetAllocationObject = budgetAllocations.get(budgetAllocation);

  const {
    displayCurrency,
    organization: { settings, locationObject },
  } = user.toJS() as User;

  const [
    nonprofit = {
      id: '',
      locationObject,
      currencyCode: displayCurrency,
      externalId: '',
      payPalModel: '',
      name: '',
      mainPicture: '',
      donateViaNonprofitId: null,
      nonprofitId: null,
    },
  ] = nonprofits;

  const {
    currencyCode: nonprofitCurrencyCode,
    externalId,
    payPalModel,
    locationObject: { countryCode },
  } = nonprofit;

  const currency = nonprofitCurrencyCode as CurrencyCode;
  useEffect(() => {
    change('currencyCode', currency ?? displayCurrency);
  }, [currency, change]);

  const nonprofitId = nonprofit?.donateViaNonprofitId || nonprofit?.nonprofitId || nonprofit?.id;
  const { data: donationProviderData, loading: donationProviderDataLoading } =
    useQuery<DonationProviderData | null>(
      () => DonationsApi.fetchDonationProviderDataForNonprofit(nonprofitId).toPromise(),
      null,
      nonprofitId,
      !nonprofitId
    );
  useEffect(() => {
    change('donationProvider', donationProviderData?.donationProvider);
  }, [donationProviderData, change]);

  const effectiveDonationProvider =
    donationProviderData?.donationProvider ?? DonationProvider.PayPal;

  const usdConversionRate = usdRates[currency as UserCurrencies];
  if (!usdConversionRate) {
    alert('There was an error fetching the currencies conversion rate');
    history.push('/donations/corporate');
  }

  const [useCustomFee, setUseCustomFee] = useState(false);

  const applicationFeeSetting =
    settings?.applicationFees?.corporate || DEFAULT_APPLICATION_FEE_CORPORATE;
  const feeCoveredBy = settings?.feeCoveredBy;

  let [, nonprofitCountryCode] = externalId?.match(/^ext-pp-([A-Z]{2})\d+$/) ?? [];
  if (nonprofitCountryCode !== null) {
    nonprofitCountryCode = countryCode || 'US';
  }

  const deedFeeCapUsd =
    user?.organization?.settings?.get('deedFeeCapUsd') || DEFAULT_DEED_FEE_CAP_USD;

  const applicationFeeAmountCap = deedFeeCapUsd / usdConversionRate;

  const calculatedFees = calculateFees({
    amount: Number(amount),
    coverFeesParam: true,
    donationProvider: effectiveDonationProvider,
    nonprofitCountryCode,
    donationPaymentMethod: PaymentMethodType.CorporateDonation,
    applicationFeeSetting,
    isSandbox: config.payPalEnv === 'SANDBOX',
    feeCoveredBy: feeCoveredBy === FeeCoveredBy.Deed ? FeeCoveredBy.Deed : FeeCoveredBy.Company,
    applicationFeeAmountCap,
    isRecurring: false,
    cardCountryCode: undefined,
  });

  const uncapped = useCustomFee
    ? {
        applicationFeeAmount: Number(customApplicationFeeAmount || 0),
        providerFeeAmount: Number(calculatedFees.providerFeeAmount || 0),
        totalFeeAmount:
          Number(customApplicationFeeAmount || 0) + Number(calculatedFees.providerFeeAmount || 0),
      }
    : calculatedFees;

  useEffect(() => {
    change(
      'customProviderFeeAmount',
      useCustomFee ? formatAmount(uncapped.providerFeeAmount || 0) : null
    );
  }, [uncapped, useCustomFee, change]);

  const corporateDonationFeeCapUSD =
    settings?.applicationFees?.corporateDonationFeeCapUSD || DEFAULT_CORPORATE_DONATION_FEE_CAP_USD;
  // NOTE-AS: useCurrency hook is reversing the rates, so we need to use `/` instead of '*'
  const feeCap =
    corporateDonationFeeCapUSD < 0
      ? Number.POSITIVE_INFINITY
      : corporateDonationFeeCapUSD / usdConversionRate;

  const { totalFeeAmount } = assertFeeCap(uncapped, feeCap);

  const totalAmount = Number(amount) + totalFeeAmount;

  const disbursementDate =
    effectiveDonationProvider &&
    estimateCorporateDonationDisbursedAt(effectiveDonationProvider, nonprofit as Organization);

  if (budgetAllocation) {
    const { currencyCode, totalIssued, limitTotal } = budgetAllocations.get(budgetAllocation);

    budgetAllocationBalance = limitTotal - totalIssued;

    if (currencyRates) {
      budgetAllocationCurrencyRate = currencyRates[currencyCode];
    }
  }
  const showBudgetCurrencyAmount =
    budgetAllocationObject && currency && currency !== budgetAllocationObject?.currencyCode;

  useEffect(() => {
    change(
      'donationTotalForValidator',
      Number.isFinite(budgetAllocationBalance)
        ? Number.isFinite(totalAmount) &&
            // NOTE-RT: useCurrency hook is reversing the rates, so we need to use `/` instead of '*'
            budgetAllocationBalance >= totalAmount / budgetAllocationCurrencyRate
        : true
    );
  }, [budgetAllocationBalance, change, totalAmount]);

  const getAmountSummarySegmentValue = (amountValue: number, currencyValue: string): string => {
    if (amountValue > 0 && currencyValue) {
      const formattedValue = formatAmount(amountValue || 0);
      return `${currencyValue} ${formattedValue}`;
    } else {
      return '-';
    }
  };

  const getBudgetAllocationValue = (allocationValue: string) => {
    const selectedBudget = budgetAllocations.get(allocationValue);
    if (selectedBudget) {
      const { title } = selectedBudget;
      return title;
    }
    return '-';
  };

  const getNonProfitValue = (nonprofitValue: Pick<Organization, 'mainPicture' | 'name' | 'id'>) => {
    const { mainPicture, name, id } = nonprofitValue;
    if (mainPicture && name && id) {
      return (
        <Chip
          variant="outlined"
          avatar={<Avatar src={mainPicture} />}
          label={name}
          onClick={() => {
            history.push(`/organizations/${id}`);
          }}
        />
      );
    } else {
      return '-';
    }
  };

  return (
    <Grid container spacing={10}>
      <Grid item md={8}>
        <div
          style={{
            paddingTop: 0,
            marginBottom: 30,
            borderRadius: 12,
            border: `1px solid ${lightGray}`,
            padding: 32,
            backgroundColor: white,
          }}
        >
          <H4>Donation details</H4>
          <div
            style={{
              marginTop: 30,
            }}
          >
            <FieldArray
              name="nonprofits"
              component={FormNonprofitsSelectorRedesign}
              limit={ALLOWED_NONPROFIT_SELECTION_LIMIT}
              getDisplayDonationProviderForNonprofit={() =>
                donationProviderDataLoading ? null : effectiveDonationProvider
              }
              required
              validate={validateNonprofits}
              acceptsDonations
              excludeDonationProviders={DEFAULT_EXCLUDE_DONATION_PROVIDERS_CORPORATE_DONATION}
            />
          </div>
          <DonationToChaptersWarningMessage
            donationProviderData={
              !donationProviderDataLoading && nonprofitId ? donationProviderData : null
            }
          />

          <div style={{ marginTop: 30 }}>
            <Field
              name="amount"
              component={FormTextFieldRedesign}
              type="number"
              required
              label="Donation Amount"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputProps: { min: 0 },
                startAdornment: (
                  <InputAdornment position="start">
                    <span>{currency}</span>
                  </InputAdornment>
                ),
              }}
              fullWidth
              variant="outlined"
              validate={validateAmount}
            />

            {user?.isAdmin && (
              <>
                <FormControlLabel
                  labelPlacement="end"
                  style={{ marginTop: 6, marginBottom: useCustomFee ? 12 : 0, marginLeft: 3 }}
                  label="Custom Application Fee"
                  control={
                    <Checkbox
                      onChange={() => {
                        setUseCustomFee(!useCustomFee);
                        if (!useCustomFee) {
                          change('customApplicationFeeAmount', null);
                          change('customProviderFeeAmount', null);
                        }
                      }}
                      value={useCustomFee}
                      defaultChecked={useCustomFee}
                    />
                  }
                />
                {useCustomFee && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Field
                      name="customApplicationFeeAmount"
                      component={FormTextFieldRedesign}
                      type="number"
                      required={useCustomFee}
                      label="Custom Application Fee Amount"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">
                            <span>{currency}</span>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      variant="outlined"
                      validate={validateAmount}
                      min={0}
                    />
                    <Field
                      name="customProviderFeeAmount"
                      component={FormTextFieldRedesign}
                      type="number"
                      required={useCustomFee}
                      label="Provider Fee Amount"
                      title="The provider fee amount is automatically calculated based on the donation amount and the donation provider and cannot be manually set."
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { min: 0, style: { cursor: 'not-allowed' } },
                        startAdornment: (
                          <InputAdornment position="start">
                            <span>{currency}</span>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      variant="outlined"
                      validate={validateAmount}
                      disabled
                      defaultValue={formatAmount(uncapped.providerFeeAmount || 0)}
                    />
                  </div>
                )}
              </>
            )}
            {totalAmount && currency !== displayCurrency ? (
              <CurrencyAmountEstimationMessage
                totalAmount={totalAmount}
                currency={currency}
                targetCurrency={displayCurrency as CurrencyCode}
                title={
                  <>
                    Estimate donation amount on your currency: <strong>{displayCurrency}</strong>
                  </>
                }
                message={
                  <>
                    The donation will be processed and receipted in <strong>{currency}</strong>{' '}
                    which is the nonprofit&apos;s currency. The amount displayed in your currency
                    here is an estimate, which will vary depending on the processing date of the
                    transaction.
                  </>
                }
              />
            ) : null}
          </div>

          <div style={{ marginTop: 30 }}>
            <Field
              name="budgetAllocation"
              component={BudgetSubEntitySelector}
              required
              label="Budget rule"
              InputLabelProps={{ shrink: true }}
              fullWidth
              subBudgets={budgetAllocations}
              budgets={budgets}
              validate={validateBudgetRule}
            />
          </div>
          {showBudgetCurrencyAmount && (
            <CurrencyAmountEstimationMessage
              totalAmount={totalAmount}
              currency={currency}
              targetCurrency={budgetAllocationObject?.currencyCode}
              title={
                <>
                  Estimate amount on budget&apos;s currency:{' '}
                  <strong>{budgetAllocationObject?.currencyCode}</strong>
                </>
              }
              message={
                <>
                  The donation will be processed and receipted in <strong>{currency}</strong> which
                  is the nonprofit&apos;s currency. The amount in the budget&apos;s currency is an
                  estimate, which will vary depending on the processing date of the transaction.
                </>
              }
            />
          )}
          <div>
            <Field name="donationTotalForValidator" component={DonationTotalForValidator} />
          </div>
          <div style={{ marginTop: 30 }}>
            <Field
              name="designation"
              component={FormTextFieldRedesign}
              helperText="Send a note to the nonprofit alongside this donation. Up to 300 characters"
              multiline
              rows={3}
              fullWidth
              validate={validateDesignation}
              label="Message to the nonprofit"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div style={{ marginTop: 30 }}>
            <Field
              name="note"
              component={FormTextFieldRedesign}
              helperText="Add any other relevant donation details. Up to 300 characters"
              multiline
              rows={3}
              validate={validateNote}
              fullWidth
              variant="outlined"
              label="Donation notes"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div style={{ marginTop: 15 }}>
            <FormControlLabel
              control={
                <Field
                  name="isAnonymous"
                  component={(props) => (
                    <Checkbox checked={props.input.value} onChange={props.input.onChange} />
                  )}
                />
              }
              label="Make this donation anonymous"
            />
          </div>
        </div>
        <DonationProviderMessage
          donationProvider={effectiveDonationProvider}
          payPalModel={payPalModel}
          countryCode={countryCode}
        />
        <div
          style={{
            paddingLeft: 25,
            paddingRight: 25,
          }}
        >
          <PrimaryButton
            type="submit"
            disabled={pristine || invalid || submitting || donationProviderDataLoading}
            onClick={() => {
              validateAmount(Number(amount), formValues);
              validateNonprofits(ImmutableList.of(nonprofits));
            }}
            variant="contained"
            styles={{
              width: 200,
            }}
          >
            Donate
          </PrimaryButton>
        </div>
      </Grid>
      <Grid item md={4}>
        <SummaryCard>
          <SummaryCardHeader headerImage={user?.organization?.mainPicture} title="Your donation" />
          <SummaryCardImage
            src="https://files.godeed.today/1626101682031_Untitled_Artwork181.png"
            alt="Thanks in advance for your contribution!"
            color={lightYellow}
          />
          <SummarySegment
            title="Estimated disbursement date"
            value={
              disbursementDate ? format(disbursementDate, convertTokens('DD MMMM, YYYY')) : '-'
            }
            tooltipText={
              disbursementDate
                ? `This date is an estimate based on ${effectiveDonationProvider}&apos;s disbursement dates and may vary`
                : undefined
            }
          />
          <SummarySegment title="To" content={getNonProfitValue(nonprofit)} />
          <SummarySegment title="Budget rule" value={getBudgetAllocationValue(budgetAllocation)} />
          <SummarySegment
            title="Total amount"
            value={getAmountSummarySegmentValue(totalAmount, currency)}
            additionalInfo={
              totalFeeAmount > 0
                ? `Includes fees ${currency} ${formatAmount(totalFeeAmount || 0)}`
                : undefined
            }
            tooltipText={
              useCustomFee
                ? `Application fee ${
                    customApplicationFeeAmount
                      ? ((Number(customApplicationFeeAmount || 0) / amount) * 100).toFixed(2)
                      : 0
                  }%, Provider fee ${
                    customProviderFeeAmount
                      ? ((Number(customProviderFeeAmount || 0) / amount) * 100).toFixed(2)
                      : 0
                  }%`
                : `${applicationFeeSetting}% support fee applies for all corporate donations`
            }
            last
          />
        </SummaryCard>
      </Grid>
    </Grid>
  );
};

export const CorporateDonationDonateForm = ({
  invalid,
  pristine,
  submitting,
  form,
  change,
}: CorporateDonationDonateFormProps): JSX.Element => {
  const user = useSelector(selectUser);
  const formValues = useSelector(getFormValues(form)) as unknown as CorporateDonationDonateValues;

  const { nonprofits = [] } = formValues?.toJS() || {};
  const locationObject = user?.organization?.locationObject?.toJS();
  const displayCurrency = user?.displayCurrency;

  const [
    nonprofit = {
      locationObject,
      currencyCode: displayCurrency,
      externalId: '',
      payPalModel: '',
    },
  ] = nonprofits;

  const { rates: usdRates } = useCurrency(UserCurrencies.USD);
  const { rates: currencyRates } = useCurrency(nonprofit.currencyCode as UserCurrencies);

  if (!user || !usdRates || !currencyRates) {
    return <LinearProgress />;
  }

  return (
    <CorporateDonationDonateFormInner
      invalid={invalid}
      pristine={pristine}
      submitting={submitting}
      form={form}
      change={change}
      user={user}
      usdRates={usdRates}
      currencyRates={currencyRates}
    />
  );
};

export default CorporateDonationDonateForm;
